import { DatePipe } from '@angular/common';
import { Table } from 'primeng/table';
import { Component, Injector, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { PageInfo } from 'src/app/core/models/page-info';
import { DropdownOptionsService } from 'src/app/core/services/dropdown-options.service';
import { deepCopy, isNullOrEmpty } from 'src/app/core/utils/common-functions';
import { SessionBaseComponent } from 'src/app/shared/components/session.base.component';
import { StockMaintenanceService } from '../../service/stock-maintenance.service';
import { TranslatePipe } from '@ngx-translate/core';
import * as xlsx from 'xlsx';
import { CalendarComponent } from 'src/app/shared/components/calendar/calendar.component';
import { PermissionConstants } from 'src/app/core/constants/permission-constants.constant';

@Component({
  selector: 'laas-stock-maintenance',
  templateUrl: './stock-maintenance.component.html',
  styleUrls: ['./stock-maintenance.component.scss']
})
export class StockMaintenanceComponent extends SessionBaseComponent implements OnInit {
  @ViewChild('destockingDate') destockingDate!: CalendarComponent;
  readonly viewPermissionCode = PermissionConstants.StockMaintenance;
  currentLang: string = this.laasSetting.language;

  isSubmitted: boolean = false;
  isImportLoading: boolean = false;
  isSearching: boolean = false;
  isSearchFormOpen: boolean = true;
  isShowImportStockDataModal: boolean = false;
  isEditMode: boolean = false;

  facilityOptions: any[] = []; // Dropdown options 倉別
  orgIdOptions: any[] = []; // Dropdown options 公司別
  brandOptions: any[] = []; // Dropdown options 品牌
  internalBrandOptions: any[] = []; // Dropdown options 內部品牌

  searchForm: FormGroup; // Form control of query

  pageInfo: PageInfo; // Table pagination information
  sortInfo: { property: string; direction: number }[] | null = null; // Table sort information

  tableColumns: any[] = []; // Table 全部的欄位
  showTableColumns: any[] = []; // Table 顯示哪些欄位

  tableValue: any[] = []; // Table data
  cloneTableValue: any[] = []; // Clone table data
  selectedRowId: any; // Table selected row ID

  @ViewChild('stockTable') dataTable!: Table;

  /**
   * 建構子
   *
   * 注入依賴、初始化 class parameter
   *
   * @param datePipe
   * @param translatePipe
   * @param formBuilder
   * @param dropdownOptionsService
   * @param stockMaintenanceService
   * @param injector
   */
  constructor(
    private injector: Injector,
    private datePipe: DatePipe,
    private translatePipe: TranslatePipe,
    private formBuilder: FormBuilder,
    private dropdownOptionsService: DropdownOptionsService,
    private stockMaintenanceService: StockMaintenanceService
  ) {
    super(injector);

    // Form control of query
    this.searchForm = this.formBuilder.group({
      brand: null,
      internalBrand: null,
      facility: null,
      storer: null,
      orgId: null,
      uId: null,
      caseId: null,
      sku: null,
      dateCodeType: [ '2'],
      dateCode: [ { value: null, disabled: true }, [ Validators.min(100000), Validators.max(999999) ] ],
      dateCodeWWStart: [ null, [ Validators.min(0), Validators.max(999) ] ],
      dateCodeWWEnd: [ null, [ Validators.min(0), Validators.max(999) ] ],
      reservedConsigneeKey: null,
      originReceiptKey: null,
      destockingDateStart: null,
      destockingDateEnd: null
    });

    // Table pagination information
    this.pageInfo = {
      pageSize: 5,
      pageIndex: 0,
      totalRecords: 0,
      totalPages: 0,
    };

    // Dropdown options 倉別
    this.dropdownOptionsService.getFacilityOptions().subscribe({
      next: (options: any[]) => {
        this.facilityOptions = options.map(x => ({
          label: x.optionDesc,
          value: x.optionCode
        }));
      }
    });

    // Dropdown options 公司別
    this.dropdownOptionsService.getLetterOrgOptions().subscribe({
      next: (options: any[]) => {
        this.orgIdOptions = options.map(x => ({ label: x.legalEntityOUName, value: x.legalEntityOUCode }));
      }
    });

    // Dropdown options 品牌
    this.dropdownOptionsService.getBrandOptions(1).subscribe({
      next: (options: any[]) => {
        this.brandOptions = options.map(x => ({ label: x.brandName, value: x.brandName }));
        this.internalBrandOptions = [...this.brandOptions];
      }
    });
  }

  /**
   * ngOnInit 屬於 Angular 生命週期的一部分，其在第一輪 ngOnChanges 完成之後調用，並且只調用一次
   *
   * 在 ngOnInit 中已經能夠取得被傳入進此元件的數據
   *
   * Angular 生命週期的 function 會在 constructor 執行完後再執行
   */
  async ngOnInit(): Promise<void> {
    await super.ngOnInit();

    // Table 全部的欄位
    this.tableColumns = [
      { field: 'facility', header: 'StockMaintenance.MainTable.Column.facility', sort: true },
      { field: 'organizationCode', header: 'StockMaintenance.MainTable.Column.organizationCode', sort: true },
      { field: 'uId', header: 'StockMaintenance.MainTable.Column.uId', sort: true },
      { field: 'brand', header: 'StockMaintenance.MainTable.Column.brand', sort: true },
      { field: 'internalBrand', header: 'StockMaintenance.MainTable.Column.internalBrand', sort: true },
      { field: 'receiptChargeType', header: 'StockMaintenance.MainTable.Column.receiptChargeType', sort: true },
      { field: 'receivedTime', header: 'StockMaintenance.MainTable.Column.receivedTime', dataType: 'date', sort: true },
      { field: 'dateCode', header: 'StockMaintenance.MainTable.Column.dateCode', sort: true },
      { field: 'vendorLot', header: 'StockMaintenance.MainTable.Column.vendorLot', sort: true },
      { field: 'originReceiptKey', header: 'StockMaintenance.MainTable.Column.originalAD', sort: true },
      { field: 'ipCapacity', header: 'StockMaintenance.MainTable.Column.ipCapacity', sort: true },
      { field: 'packageCode', header: 'StockMaintenance.MainTable.Column.packageCode', sort: true },
      { field: 'hue', header: 'StockMaintenance.MainTable.Column.hue', sort: true },
      { field: 'dfName', header: 'StockMaintenance.MainTable.Column.dfName', sort: true },
      { field: 'reservedOrgId', header: 'StockMaintenance.MainTable.Column.reservedOrgId', sort: true },
      { field: 'reservedConsigneeKey', header: 'StockMaintenance.MainTable.Column.uidLockCode', sort: true },
      { field: 'supplierRegion', header: 'StockMaintenance.MainTable.Column.supplierRegion', sort: true },
      { field: 'uidLockTime', header: 'StockMaintenance.MainTable.Column.uidLockTime', sort: true },
      { field: 'uidUnlockTime', header: 'StockMaintenance.MainTable.Column.uidUnlockTime', sort: true },
      { field: 'lockReason', header: 'StockMaintenance.MainTable.Column.lockReason', sort: true },
      { field: 'storer', header: 'StockMaintenance.MainTable.Column.storer', sort: true },
      { field: 'orgId', header: 'StockMaintenance.MainTable.Column.orgId', sort: true },
      { field: 'caseId', header: 'StockMaintenance.MainTable.Column.caseId', sort: true },
      { field: 'sku', header: 'StockMaintenance.MainTable.Column.sku', sort: true },
      { field: 'vendorDc', header: 'StockMaintenance.MainTable.Column.vendorDc', sort: true },
      { field: 'coo', header: 'StockMaintenance.MainTable.Column.coo', sort: true },
      { field: 'iipCapacity', header: 'StockMaintenance.MainTable.Column.iipCapacity', sort: true },
      { field: 'caseCapacity', header: 'StockMaintenance.MainTable.Column.caseCapacity', sort: true },
      { field: 'revision', header: 'StockMaintenance.MainTable.Column.revision', sort: true },
      { field: 'kitNo', header: 'StockMaintenance.MainTable.Column.kitNo', sort: true },
      { field: '2DBarcode', header: 'StockMaintenance.MainTable.Column.2DBarcode', sort: true },
      { field: 'reservedSubinventory', header: 'StockMaintenance.MainTable.Column.reservedSubinventory', sort: true },
      { field: 'reservedOthers', header: 'StockMaintenance.MainTable.Column.reservedOthers', sort: true },
      { field: 'lockStaff', header: 'StockMaintenance.MainTable.Column.lockStaff', sort: true },
      { field: 'unlockStaff', header: 'StockMaintenance.MainTable.Column.unlockStaff', sort: true },
      { field: 'lockType', header: 'StockMaintenance.MainTable.Column.lockType', sort: true },
      { field: 'othersReasonDesc', header: 'StockMaintenance.MainTable.Column.othersReasonDesc', sort: true },
      { field: 'imcCode', header: 'StockMaintenance.MainTable.Column.imcCode', sort: true },
      { field: 'onhandQty', header: 'StockMaintenance.MainTable.Column.onhandQty', sort: true },
      { field: 'trackingItemsNotes', header: 'StockMaintenance.MainTable.Column.trackingItemsNotes', sort: true },
      { field: 'destockingDate', header: 'StockMaintenance.MainTable.Column.destockingDate', dataType: 'date', sort: true },
      { field: 'lastUpdateDate', header: 'StockMaintenance.MainTable.Column.lastUpdateDate', dataType: 'date', sort: true },
      { field: 'lastUpdatedBy', header: 'StockMaintenance.MainTable.Column.lastUpdateUser', sort: true }
    ];

    // Table 顯示哪些欄位
    this.showTableColumns = this.tableColumns;

  }

  /**
   * 將編輯中的 row 變為檢視狀態
   *
   * @returns
   */
  cancelRowEdit(): void {
    if (isNullOrEmpty(this.selectedRowId)) {
      return;
    }

    const val = this.tableValue[this.selectedRowId];
    this.dataTable.cancelRowEdit(val);
  }

  /**
   * 檢查 row 的資料是否符合規則
   *
   * @returns
   */
  isRowInvalid(): boolean {

    if (isNullOrEmpty(this.selectedRowId)) {
      return true;
    }

    const val = this.tableValue[this.selectedRowId];
    const cloneVal = this.cloneTableValue[this.selectedRowId];

    // 資料是否有異動
    if(JSON.stringify(val) != JSON.stringify(cloneVal)) {

      // 規則: destockingDate is required
      if (isNullOrEmpty(val.destockingDate)) {
        this.toastService.warn(this.translateService.instant('StockMaintenance.Message.NoDestockingDate'));
        return true; // 資料有錯誤
      }
    }

    return false; // 資料無錯誤
  }

  /**
   * 將每行資料加上 row ID 以便識別，且 row ID 其實就是 array index
   *
   * @param array
   */
  putRowId(array: any[]) {
    let rowId = 0;
    for (const val of array) {
      val.rowId = rowId;
      rowId = rowId + 1;
    }
  }

  /**
   * 下載 Stock Data Excel 檔案
   *
   * @param excelStockDatas
   */
  downloadExcel(excelStockDatas: any[]): void {

    const dataRows = [];

    // Loop for produce all row
    for (const stockData of excelStockDatas) {

      const row = {} as any;
      const columns = [...this.tableColumns];

      // Loop for produce one row
      for (const column of columns) {

        let fieldName = column.field;
        if (!stockData.hasOwnProperty(fieldName)) {
          continue;
        }

        let value = stockData[fieldName];
        switch (fieldName) {
          case 'facility':
            value = this.facilityOptions.find(x => x.value === value)?.label;
            break;
          case 'reservedOrgId':
            value = this.orgIdOptions.find(x => x.value === value)?.label;
            break;
          case 'orgId':
            value = this.orgIdOptions.find(x => x.value === value)?.label;
            break;
          case 'receivedTime':
            value = this.datePipe.transform(value, 'yyyy/MM/dd HH:mm:ss', this.laasSetting.facilityTimeZone);
            break;
          case 'destockingDate':
            value = this.datePipe.transform(value, 'yyyy/MM/dd', this.laasSetting.facilityTimeZone);
            break;
          case 'lastUpdateDate':
          value = this.datePipe.transform(value, 'yyyy/MM/dd HH:mm:ss', this.laasSetting.facilityTimeZone);
          break;
        }

        const headerName = this.translatePipe.transform(column.header);
        row[headerName] = value;
      }

      dataRows.push(row);
    }

    // Prepare excel
    const worksheet = xlsx.utils.json_to_sheet(dataRows);
    const workbook = { Sheets: { data: worksheet }, SheetNames: ['data'] };
    const excelBuffer: any = xlsx.write(workbook, { bookType: 'xlsx', type: 'array' });
    const data: Blob = new Blob(
      [excelBuffer],
      { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8' }
    );

    // Download excel
    const url = URL.createObjectURL(data);
    const linkEle = document.createElement('a');
    linkEle.href = url;
    linkEle.download = 'Stock Data';
    linkEle.click();

    // Remove temporary link href
    URL.revokeObjectURL(url);
    linkEle.remove();
  }

  /**
   * Get table 顯示哪些欄位
   */
  get selectedColumns(): any[] {
    return this.showTableColumns;
  }

  /**
   * Set table 顯示哪些欄位
   */
  set selectedColumns(cols: any[]) {
    this.showTableColumns = this.tableColumns.filter(col => cols.includes(col));
  }

  /**
   * Get select row of table
   */
  get selectRow(): any {
    if (isNullOrEmpty(this.selectedRowId)) {
      return null;
    }
    return this.tableValue[this.selectedRowId];
  }

  /**
   * Set select row of table
   */
  set selectRow(rowData: any) {

    // 這支程式只承認 selected, 不承認 unselected
    if (isNullOrEmpty(rowData)) {
      return;
    }

    // 若原本就是 null or undefined, 則只需接值即可.
    if (isNullOrEmpty(this.selectedRowId)) {
      this.selectedRowId = rowData.rowId;
      return;
    }

    // 若 selected 同一行，不用做任何事
    if (this.selectedRowId == rowData.rowId) {
      return;
    }

    // 驗證資料
    if (this.isRowInvalid()) {
      return;
    }

    if (this.isEditMode) {
      this.cancelRowEdit();
      this.dataTable.initRowEdit(rowData);
    }

    this.selectedRowId = rowData.rowId;
  }

  /**
   * Table 換頁
   *
   * @param event
   */
  tablePageChange(event: any) {
    this.pageInfo.pageSize = event.rows;
    this.pageInfo.pageIndex = event.first / this.pageInfo.pageSize;
    this.requestOfQuery();
  }

  /**
   * Table 排序
   *
   * @param event
   */
  tableSort(event: any): void {
    this.sortInfo = [ { property: event.field, direction: event.order } ];
    this.requestOfQuery();
  }

  /**
   * Button 查詢
   *
   * @param event
   */
  searchBtnClick(event: any) {
    this.pageInfo.pageIndex = 0;

  if (!this.IsInputValid()) {
    this.toastService.warn(this.translateService.instant('StockMaintenance.Message.CheckInputValid'));
    return;
  }

    this.requestOfQuery();
  }

  IsInputValid(): boolean {
    const brand = this.searchForm.controls['brand'].value;
    const internalBrand = this.searchForm.controls['internalBrand'].value;

    return (brand || internalBrand);
  }

  /**
   * Button 匯出
   *
   * @param event
   */
  exportBtnClick(event: any): void {
    this.requestOfQuery(false);
  }

  /**
   * HTTP request of query
   *
   * @param isQuery
   * @returns
   */
  requestOfQuery(isQuery: boolean = true) {
    this.isSubmitted = true;

    if (this.searchForm.invalid) {
      return;
    }

    let searchParams = deepCopy(this.searchForm.value);
    this.isSearching = true;

    if (isQuery) {

      // Pagination information
      searchParams.pageSize = this.pageInfo.pageSize;
      searchParams.pageIndex = this.pageInfo.pageIndex;
      searchParams.sortInfo = this.sortInfo;

      // 將編輯中的 row 變為檢視狀態
      if (this.isEditMode) {
        this.cancelRowEdit();
        this.isEditMode = false;
      }

      // Clear selected row ID
      this.selectedRowId = undefined;

      this.stockMaintenanceService.queryStockData(searchParams)
        .subscribe({
          next: (result) => {
            const isSuccess = result.success;

            if (!isSuccess) {
              this.toastErrorMessage(result.message);

              this.tableValue = [];
              this.cloneTableValue = [];
              this.pageInfo = {
                pageSize: 5,
                pageIndex: 0,
                totalRecords: 0,
                totalPages: 0,
              };

              return;
            }

            // 共 ? 筆
            this.pageInfo.totalRecords = result.total;

            // Get stock data
            const stockDatas = result.stockDatas || [];

            // Initial table value and put row ID
            this.tableValue = deepCopy(stockDatas) || [];
            this.putRowId(this.tableValue);
            this.cloneTableValue = deepCopy(this.tableValue) || [];

            // Select row 1
            if (this.tableValue.length > 0) {
              this.selectedRowId = this.tableValue[0].rowId;
            }
          },
          error: (err) => {
            this.toastErrorMessage(err.error);

            this.tableValue = [];
            this.cloneTableValue = [];
            this.pageInfo = {
              pageSize: 5,
              pageIndex: 0,
              totalRecords: 0,
              totalPages: 0,
            };
          },
          complete: () => {
            this.isSearching = false;
            this.isSearchFormOpen = false;
          }
        });

    } else {

      this.stockMaintenanceService.exportStockData(searchParams)
        .subscribe({
          next: (result) => {
            const isSuccess = result.success;

            if (!isSuccess) {
              this.toastErrorMessage(result.message);
              return;
            }

            this.downloadExcel(result.stockDatas);
          },
          error: (err) => {
            this.toastErrorMessage(err.error);
          },
          complete: () => {
            this.isSearching = false;
          }
        });
    }
  }

  /**
   * OPEN|CLOSE Import Stock Data Dialog
   *
   * @param val
   */
  visibleChangeOfStockDataImportModal(val: boolean): void {
    this.isShowImportStockDataModal = val;
  }

  /**
   * Button 切換 [檢視/編輯]
   */
  switchEditMode(): void {

      // 將被編輯 row 變為檢視狀態
    if (this.isEditMode) {
      this.cancelRowEdit();
    }

    // 切換 edit mode
    this.isEditMode = !this.isEditMode;

    // 若為編輯狀態，且有 select row
    if (this.isEditMode && !isNullOrEmpty(this.selectedRowId)) {
      const val = this.tableValue[this.selectedRowId];
      this.dataTable.initRowEdit(val);
    }
  }

  /**
   * Button 還原
   */
  resetTableValue(): void {
    this.confirmationService.confirm({
      header: this.translateService.instant('Common.Toast.Warn'),
      message: this.translateService.instant('StockMaintenance.Message.Restore'),
      icon: 'pi pi-info-circle',
      accept: () => {

        // Restore table data
        this.tableValue = deepCopy(this.cloneTableValue) || [];
      }
    });
  }

  /**
   * Button 儲存
   */
  saveBtnClick(): void {
    this.confirmationService.confirm({
      header: this.translateService.instant('Common.Toast.Warn'),
      message: this.translateService.instant('StockMaintenance.Message.Save'),
      icon: 'pi pi-info-circle',
      accept: () => {
        this.requestOfSave();
      }
    });
  }

  /**
   * HTTP request of save
   *
   * @returns
   */
  requestOfSave() {

    // 驗證資料
    if (!this.isEditMode || this.isRowInvalid()) {
      return
    }

    let updateDataArr = [];

    for (const rowVal of this.tableValue) {
      for (const cloneRowVal of this.cloneTableValue) {
        if(rowVal.rowId == cloneRowVal.rowId) {
          if(JSON.stringify(rowVal) != JSON.stringify(cloneRowVal)) {
            rowVal.destockingDate = this.datePipe.transform(rowVal.destockingDate, 'yyyy/MM/dd', this.laasSetting.facilityTimeZone);
            rowVal.userId = this.userProfile.email;
            updateDataArr.push(rowVal);
          }
        }
      }
    }

    if (updateDataArr.length === 0) {
      return;
    }

    const saveParameter = {
      iTrackingItems: updateDataArr
    };

    this.stockMaintenanceService.saveTrackingItem(saveParameter)
      .subscribe({
        next: (result) => {
          const isSuccess = result.success;

          if (!isSuccess) {
            this.toastErrorMessage(result.message);
            return;
          }

          this.toastService.info(result.message);

          // 重新查詢資料
          this.requestOfQuery();
        },
        error: (err) => {
          this.toastErrorMessage(err.error);
        }
      });
  }

  /**
   * On change Next Tracking Date
   *
   * @param event
   * @param rowData
   */
  changeTrackingDate(event: any, rowData: any): void {
    rowData.destockingDate = event;
  }

  /**
   * Enable Date Code
   */
   dateCodeType1(): void {
    this.searchForm.controls.dateCode.enable();
    this.searchForm.controls.dateCodeWWStart.disable();
    this.searchForm.controls.dateCodeWWEnd.disable();
  }

  /**
   * Enable Date Code Start & Date Code End
   */
   dateCodeType2(): void {
    this.searchForm.controls.dateCode.disable();
    this.searchForm.controls.dateCodeWWStart.enable();
    this.searchForm.controls.dateCodeWWEnd.enable();
   }

  dateOnChange(date: any, type: string) {
    switch (type) {
      case "destockingDate":
        const formattedStartDate = this.datePipe.transform(date.startDate, 'yyyy/MM/dd', this.laasSetting.facilityTimeZone);
        this.searchForm.controls.destockingDateStart.setValue(formattedStartDate);
        const formattedEndDate = this.datePipe.transform(date.endDate, 'yyyy/MM/dd', this.laasSetting.facilityTimeZone);
        this.searchForm.controls.destockingDateEnd.setValue(formattedEndDate);
        break;
    }
  }
}
